import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, EmptyState, ErrorNotice, Modal, SearchInput, useDebouncedValue } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { createProposal, searchOpportunities, type PickerOpportunity } from '@/proposals/lib/api';
import { newProposalDocument } from '@/proposals/lib/template';
import { formatCount, formatShortDate } from '@/proposals/lib/money';

export interface NewProposalDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called with the new proposal's id once it exists. */
  readonly onCreated: (id: string) => void;
}

const SEARCH_DEBOUNCE_MS = 250;
const UNTITLED = 'Untitled proposal';

function where(opportunity: PickerOpportunity): string {
  return [opportunity.eventCity, opportunity.eventState].filter((s) => s !== '').join(', ');
}

/**
 * Start a proposal from one of the caller's open opportunities — the server
 * seeds the client, event and salesperson into the BCE template — or from the
 * bare template when there is no opportunity yet.
 */
export function NewProposalDialog({ open, onClose, onCreated }: NewProposalDialogProps): React.ReactElement {
  const [term, setTerm] = useState('');
  const debounced = useDebouncedValue(term.trim(), SEARCH_DEBOUNCE_MS);

  const results = useQuery({
    queryKey: ['proposal-opportunities', debounced],
    queryFn: ({ signal }) => searchOpportunities(debounced, signal),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (opportunityId: string | null) =>
      createProposal(
        opportunityId
          ? { opportunityId, document: newProposalDocument(UNTITLED) }
          : { name: UNTITLED, document: newProposalDocument(UNTITLED) },
      ),
    onSuccess: (proposal) => onCreated(proposal.id),
  });

  const items = results.data?.items ?? [];

  return (
    <Modal
      open={open}
      title="New proposal"
      subtitle="Pick the opportunity this proposal is for. The client, event and your details are filled in for you."
      onClose={onClose}
      dismissible={!create.isPending}
      className="max-w-2xl"
      phoneFullScreen
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => create.mutate(null)} disabled={create.isPending}>
            Start without an opportunity
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SearchInput
          label="Search your opportunities"
          placeholder="Client, opportunity, program or invoice"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          autoFocus
        />

        {create.isError ? (
          <ErrorNotice inline title="Could not create the proposal" message={errorMessage(create.error)} />
        ) : null}

        {results.isPending ? (
          <p className="text-portal-small text-portal-muted">Loading opportunities…</p>
        ) : results.isError ? (
          <ErrorNotice inline title="Opportunities unavailable" message={errorMessage(results.error)} />
        ) : results.data.unresolvedOwner ? (
          <ErrorNotice
            inline
            tone="warning"
            message="We couldn't match you to a Salesforce user, so your opportunities can't be listed until your bcecloud email matches your Salesforce login. Ask an admin, or start without an opportunity."
          />
        ) : items.length === 0 ? (
          <EmptyState
            title="No open opportunities match"
            message={debounced ? 'Try the client or program name.' : 'You have no open opportunities right now.'}
          />
        ) : (
          <ul
            aria-label="Opportunities"
            className="max-h-80 max-md:max-h-none divide-y divide-portal-line overflow-y-auto rounded-portal-card border border-portal-line"
          >
            {items.map((opportunity) => (
              <li key={opportunity.id}>
                <button
                  type="button"
                  className="flex min-h-12 w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-portal-mist disabled:opacity-60"
                  disabled={create.isPending}
                  onClick={() => create.mutate(opportunity.id)}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-portal-small font-bold">{opportunity.accountName || opportunity.name}</span>
                    <span className="block truncate text-portal-micro text-portal-muted">
                      {[opportunity.name, opportunity.programName, where(opportunity)].filter((s) => s !== '').join(' · ')}
                    </span>
                  </span>
                  <span className="shrink-0 text-right text-portal-micro text-portal-muted whitespace-nowrap">
                    {formatShortDate(opportunity.eventDate)}
                    {opportunity.participants ? ` · ${formatCount(opportunity.participants)} people` : ''}
                    <span className="block">{opportunity.stageName}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
